import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Added for attachments
import { Box, Typography, Avatar, IconButton, InputBase, Paper, CircularProgress, Popover, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions'; // Added Emoji Icon
import EmojiPicker from 'emoji-picker-react'; // Import Emoji Picker
import { db, auth, storage } from '../firebase';
import BottomNavigationBar from './BottomNavigationBar';

const ChatScreen = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState({ name: 'Loading...', profilePictureUrl: 'https://via.placeholder.com/40' });
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false); // Loading state for files
  const messagesEndRef = useRef(null);

  // Emoji Popover state
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) { navigate('/signin'); return; }

    const fetchOtherUser = async () => {
      try {
        const chatDoc = await getDoc(doc(db, 'chats', chatId));
        if (chatDoc.exists()) {
          const otherUserId = chatDoc.data().participants?.find(id => id !== currentUser.uid);
          const userDoc = await getDoc(doc(db, 'users', otherUserId));
          if (userDoc.exists()) {
            setOtherUser({
              name: userDoc.data().firstName || 'User',
              profilePictureUrl: userDoc.data().profilePictureUrl || 'https://via.placeholder.com/40',
            });
          }
        }
      } catch (err) { console.error(err); }
    };

    const messagesQuery = query(collection(db, 'chats', chatId, 'messages'), orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setTimeout(scrollToBottom, 100);
    });

    fetchOtherUser();
    return () => unsubscribe();
  }, [chatId]);

  // --- Send Message Logic ---
  const handleSendMessage = async (text = newMessage, fileUrl = null, fileType = null) => {
    if (!text.trim() && !fileUrl) return;

    const currentUser = auth.currentUser;
    try {
      const messageData = {
        senderId: currentUser.uid,
        timestamp: serverTimestamp(),
        read: false,
      };

      if (fileUrl) {
        messageData.fileUrl = fileUrl;
        messageData.fileType = fileType;
        messageData.text = text || "Sent an attachment";
      } else {
        messageData.text = text;
      }

      await addDoc(collection(db, 'chats', chatId, 'messages'), messageData);
      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage: fileUrl ? "Sent an attachment" : text,
        lastMessageTime: serverTimestamp(),
      });

      setNewMessage('');
    } catch (err) { console.error(err); }
  };

  // --- Attachment Logic ---
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileRef = ref(storage, `chats/${chatId}/${Date.now()}_${file.name}`);
      const uploadTask = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(uploadTask.ref);
      
      const type = file.type.startsWith('image/') ? 'image' : 'file';
      handleSendMessage('', downloadURL, type);
    } catch (err) {
      alert("Failed to upload file.");
    } finally {
      setUploading(false);
    }
  };

  // --- Emoji Logic ---
  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <IconButton onClick={() => navigate('/messages')}><ArrowBackIcon sx={{ color: '#FFF' }} /></IconButton>
        <Avatar src={otherUser.profilePictureUrl} sx={styles.avatar} />
        <Typography sx={styles.headerTitle}>{otherUser.name}</Typography>
      </Box>

      <Box sx={styles.messagesContainer}>
        {messages.map((msg) => (
          <Box key={msg.id} sx={{ ...styles.messageBubble, ...(msg.senderId === auth.currentUser?.uid ? styles.sentMessage : styles.receivedMessage) }}>
            {msg.fileUrl && (
              msg.fileType === 'image' ? (
                <Box component="img" src={msg.fileUrl} sx={styles.messageImage} onClick={() => window.open(msg.fileUrl)} />
              ) : (
                <Button variant="outlined" size="small" href={msg.fileUrl} target="_blank" sx={{ mb: 1 }}>View File</Button>
              )
            )}
            <Typography sx={styles.messageText}>{msg.text}</Typography>
            <Typography sx={styles.messageTime}>{msg.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box sx={styles.inputContainer}>
        <Paper sx={styles.inputPaper}>
          <IconButton onClick={(e) => setEmojiAnchorEl(e.currentTarget)}>
            <EmojiEmotionsIcon sx={{ color: '#888' }} />
          </IconButton>
          
          <IconButton component="label">
            <AttachFileIcon sx={{ color: '#888' }} />
            <input type="file" hidden onChange={handleFileChange} />
          </IconButton>

          <InputBase
            sx={styles.input}
            placeholder={uploading ? "Uploading..." : "Type a message..."}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            multiline
            disabled={uploading}
          />

          <IconButton onClick={() => handleSendMessage()} disabled={uploading}>
            {uploading ? <CircularProgress size={24} /> : <SendIcon sx={{ color: '#C00068' }} />}
          </IconButton>
        </Paper>
      </Box>

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={() => setEmojiAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <EmojiPicker onEmojiClick={onEmojiClick} />
      </Popover>
    </Box>
  );
};

const styles = {
  container: { flex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#E5DDD5', pb: '60px' },
  header: { background: 'linear-gradient(to right, #B200BB, #C00068)', padding: '10px 16px', display: 'flex', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1000 },
  headerTitle: { color: '#FFF', fontSize: '18px', fontWeight: '600', ml: '10px', flex: 1 },
  avatar: { width: '40px', height: '40px', border: '2px solid #FFF' },
  messagesContainer: { flex: 1, overflowY: 'auto', p: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  messageBubble: { maxWidth: '75%', borderRadius: '12px', p: '8px 12px', position: 'relative', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
  sentMessage: { backgroundColor: '#DCF8C6', alignSelf: 'flex-end' },
  receivedMessage: { backgroundColor: '#FFF', alignSelf: 'flex-start' },
  messageImage: { maxWidth: '100%', borderRadius: '8px', cursor: 'pointer', mb: 1 },
  messageText: { fontSize: '15px', color: '#333' },
  messageTime: { fontSize: '10px', color: '#888', textAlign: 'right', mt: 0.5 },
  inputContainer: { p: '10px', backgroundColor: '#F0F2F5' },
  inputPaper: { display: 'flex', alignItems: 'center', borderRadius: '25px', p: '4px 10px' },
  input: { flex: 1, ml: 1 },
};

export default ChatScreen;